import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';

// eslint-disable-next-line import-x/named
import type { createOrUpdateSo5LineupInput } from '@sorare/core/src/__generated__/globalTypes';
import { JONUM_VERIFICATION_REQUIRED_CODE } from '@sorare/core/src/components/jonum/JonumVerificationDialog/useJonumVerificationGuard';
import { DialogKey } from '@sorare/core/src/components/navigation/WithDialogs';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useMutation } from '@sorare/core/src/hooks/graphql/useMutation';
import { useOpenDialog } from '@sorare/core/src/hooks/navigation/useOpenDialog';

import { useHandleComposeTeamErrors } from '#src/hooks/compose-team/useHandleComposeTeamErrors.ts';

import type {
  CreateOrUpdateSo5LineupMutation,
  CreateOrUpdateSo5LineupMutationVariables,
} from './__generated__/useCreateOrUpdateLineup.graphql.ts';

const CREATE_OR_UPDATE_SO5_LINEUP_MUTATION = gql`
  mutation CreateOrUpdateSo5LineupMutation(
    $input: createOrUpdateSo5LineupInput!
    $so5LeaderboardSlug: String!
    $sport: Sport!
  ) {
    createOrUpdateSo5Lineup(input: $input) {
      so5Lineup {
        id
        so5Appearances(includeSubs: true) {
          id
          anyCard {
            slug
            concurrentSo5Lineups(so5LeaderboardSlug: $so5LeaderboardSlug) {
              id
            }
          }
        }
      }
      so5LeaderboardContender {
        slug
        so5Leaderboard {
          slug
          so5Fixture {
            slug
            so5LeaderboardGroups {
              slug
              eligibleCardsCountByPosition {
                slug
                position
                seasonality
                totalCount
                usedCardsCount
              }
              so5LeagueTracks {
                slug
                entrySo5Leaderboard {
                  slug
                  canCompose {
                    value
                  }
                }
                so5Leaderboards {
                  slug
                  mySo5LeaderboardContenders(includeHiddenManagerTeam: true) {
                    slug
                  }
                }
              }
              # Update the cache for the lobby to add the newly created team slot
              # and make the scroll to that team work post compose team
              mySo5LeaderboardContenders {
                slug
              }
            }
          }
        }
      }
      currentUser {
        slug
        forcedIntoOnboarding(sport: $sport)
        blockchainCardsInLineups
      }
      errors {
        path
        message
        code
      }
    }
  }
` as TypedDocumentNode<
  CreateOrUpdateSo5LineupMutation,
  CreateOrUpdateSo5LineupMutationVariables
>;

export const useCreateOrUpdateLineupMutation = () => {
  const { sport } = useSportContext();
  const openDialog = useOpenDialog();
  const [createOrUpdateSo5Lineup, { loading }] = useMutation(
    CREATE_OR_UPDATE_SO5_LINEUP_MUTATION,
    {
      // Both flags off + showErrorsInForm: true makes useMutation suppress
      // its automatic error handling (no snack, no thrown MutationError) so
      // the wrapper below can filter 25003 (handled by the jonum dialog) and
      // re-dispatch the rest through useHandleComposeTeamErrors.
      showErrorsWithSnackNotification: false,
      showErrorsInForm: true,
    }
  );
  const handleErrors = useHandleComposeTeamErrors();

  const submit = async ({
    input,
    so5LeaderboardSlug,
  }: {
    input: createOrUpdateSo5LineupInput;
    so5LeaderboardSlug: string;
  }) => {
    const result = await createOrUpdateSo5Lineup({
      variables: { input, so5LeaderboardSlug, sport },
    });
    const allErrors = (result.errors || []).map(error => ({
      path: null,
      message: error.message,
      code: error.code ?? null,
    }));
    const errors = allErrors.filter(
      error => error.code !== JONUM_VERIFICATION_REQUIRED_CODE
    );
    if (errors.length < allErrors.length) {
      openDialog(DialogKey.jonumVerification, 'true');
    }
    if (!errors.length) {
      return {
        data: result.data,
        handledErrors: [],
        unhandledErrors: [],
      };
    }

    const { handledErrors, unhandledErrors } = handleErrors(errors);

    return {
      data: result.data,
      handledErrors,
      unhandledErrors,
    };
  };
  return { submit, loading };
};

export const useCreateOrUpdateLineup = ({
  so5LeaderboardSlug,
}: {
  so5LeaderboardSlug: string;
}) => {
  const { submit, loading } = useCreateOrUpdateLineupMutation();
  const submitWithSlug = async (input: createOrUpdateSo5LineupInput) =>
    submit({ input, so5LeaderboardSlug });

  return { submit: submitWithSlug, loading };
};
